import { createReducer } from "typesafe-actions";

import {
  ChatViewContextActions,
  clearMapQuery,
  clearPlannerQuery,
  detachViewContext,
  pinViewContext,
  registerViewContext,
  resetViewContext,
  setListQuery,
  setMapPanelQuery,
  setMapQuery,
  setPlannerQuery,
  setTerritoryPinsBounds,
  setTerritoryQuery,
  unpinViewContext,
  unregisterViewContext,
} from "./actions";
import composeViewContext from "./composeViewContext";
import ViewContextState, { ViewContextEntry } from "./ViewContextState";

export const chatViewContextInitialState: ViewContextState = {
  entries: [],
  listQueries: {},
  plannerQueries: {},
};

const isSameEntry = (left: ViewContextEntry, right: ViewContextEntry) =>
  left.anchorKey === right.anchorKey &&
  left.label === right.label &&
  left.contextKey === right.contextKey;

// While its anchor is shown, the pin follows the live page.
const withEntries = (state: ViewContextState, entries: ViewContextEntry[]): ViewContextState => {
  const composed = composeViewContext(entries);
  const followsLive = !!state.pinned && composed?.anchorKey === state.pinned.anchorKey;
  return { ...state, entries, ...(followsLive ? { pinned: composed } : {}) };
};

const chatViewContext = createReducer<ViewContextState, ChatViewContextActions>(
  chatViewContextInitialState
)
  .handleAction(registerViewContext, (state, { payload }) => {
    const index = state.entries.findIndex(({ instanceId }) => instanceId === payload.instanceId);
    if (index < 0) {
      return withEntries(state, [...state.entries, payload]);
    }
    if (isSameEntry(state.entries[index], payload)) {
      return state;
    }
    return withEntries(state, state.entries.with(index, payload));
  })
  .handleAction(setListQuery, (state, { payload: { entityType, ...query } }) => ({
    ...state,
    listQueries: { ...state.listQueries, [entityType]: query },
  }))
  .handleAction(setMapQuery, (state, { payload }) => ({ ...state, mapQuery: payload }))
  .handleAction(setMapPanelQuery, (state, { payload }) => ({ ...state, mapPanelQuery: payload }))
  .handleAction(clearMapQuery, (state) =>
    state.mapQuery || state.mapPanelQuery
      ? { ...state, mapPanelQuery: undefined, mapQuery: undefined }
      : state
  )
  .handleAction(clearPlannerQuery, (state, { payload }) => ({
    ...state,
    plannerQueries: { ...state.plannerQueries, [payload]: null },
  }))
  .handleAction(setPlannerQuery, (state, { payload: { view, ...query } }) => ({
    ...state,
    plannerQueries: { ...state.plannerQueries, [view]: query },
  }))
  .handleAction(setTerritoryQuery, (state, { payload }) => ({ ...state, territoryQuery: payload }))
  .handleAction(setTerritoryPinsBounds, (state, { payload }) => ({
    ...state,
    territoryPinsBounds: payload,
  }))
  .handleAction(unregisterViewContext, (state, { payload }) => {
    const entries = state.entries.filter(({ instanceId }) => instanceId !== payload);
    return entries.length === state.entries.length ? state : withEntries(state, entries);
  })
  .handleAction(detachViewContext, (state, { payload }) => {
    const composed = composeViewContext(state.entries);
    // Detaching a pin drops it and detaches the page beneath, so the bar reads "removed".
    if (state.pinned) {
      return state.pinned.anchorKey === payload
        ? { ...state, detachedAnchorKey: composed?.anchorKey, pinned: undefined }
        : state;
    }
    return composed?.anchorKey === payload ? { ...state, detachedAnchorKey: payload } : state;
  })
  .handleAction(pinViewContext, (state, { payload }) => {
    const composed = composeViewContext(state.entries);
    if (state.pinned || composed?.anchorKey !== payload) {
      return state;
    }
    return { ...state, detachedAnchorKey: undefined, pinned: composed };
  })
  .handleAction(unpinViewContext, (state) =>
    state.pinned ? { ...state, pinned: undefined } : state
  )
  .handleAction(resetViewContext, (state) =>
    state.detachedAnchorKey === undefined && !state.pinned
      ? state
      : { ...state, detachedAnchorKey: undefined, pinned: undefined }
  );

export default chatViewContext;
